import Placeholder from '@tiptap/extension-placeholder';
import { type Editor, EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import React from 'react';
import { cn } from '@/lib/utils';

/**
 * Editor rico da seção "02 Descrição do batalhão" (print 03).
 * TipTap + StarterKit com heading 2/3 — as MESMAS tags do sanitize no backend
 * (p, h2, h3, strong, em, s, ul, ol, li, blockquote, br, hr). Não habilite extensões
 * que gerem outras tags sem atualizar o allowlist de `bataillon.service.ts`.
 *
 */
export const RichTextEditor: React.FC<{
  value: string | null;
  onChange: (html: string) => void;
  placeholder?: string;
}> = ({
  value,
  onChange,
  placeholder = 'Descreva o propósito e as funções do batalhão...',
}) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] } }),
      Placeholder.configure({ placeholder }),
    ],
    content: value ?? '',
    immediatelyRender: false,
    editorProps: { attributes: { class: 'bt-prose min-h-[220px] outline-none' } },
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
  });

  // Troca de batalhão na mesma tela: sincroniza sem disparar onUpdate.
  React.useEffect(() => {
    if (editor && value !== editor.getHTML())
      editor.commands.setContent(value ?? '', { emitUpdate: false });
  }, [editor, value]);

  return (
    <div className="overflow-hidden">
      <Toolbar editor={editor} />
      <div className="px-[26px] pb-[26px] pt-[22px]">
        <EditorContent editor={editor} />
      </div>
    </div>
  );
};

type Tool = {
  label: string;
  title: string;
  active?: (e: Editor) => boolean;
  run: (e: Editor) => void;
  className?: string;
  gap?: boolean;
};
const TOOLS: Tool[] = [
  {
    label: 'Parágrafo',
    title: 'Texto',
    active: (e) => e.isActive('paragraph'),
    run: (e) => e.chain().focus().setParagraph().run(),
  },
  {
    label: 'Título',
    title: 'Título',
    active: (e) => e.isActive('heading', { level: 2 }),
    run: (e) => e.chain().focus().toggleHeading({ level: 2 }).run(),
  },
  {
    label: 'Subtítulo',
    title: 'Subtítulo',
    active: (e) => e.isActive('heading', { level: 3 }),
    run: (e) => e.chain().focus().toggleHeading({ level: 3 }).run(),
  },
  {
    label: 'B',
    title: 'Negrito',
    active: (e) => e.isActive('bold'),
    run: (e) => e.chain().focus().toggleBold().run(),
    className: 'font-extrabold',
    gap: true,
  },
  {
    label: 'I',
    title: 'Itálico',
    active: (e) => e.isActive('italic'),
    run: (e) => e.chain().focus().toggleItalic().run(),
    className: 'italic',
  },
  {
    label: 'S',
    title: 'Tachado',
    active: (e) => e.isActive('strike'),
    run: (e) => e.chain().focus().toggleStrike().run(),
  },
  {
    label: 'Lista',
    title: 'Lista',
    active: (e) => e.isActive('bulletList'),
    run: (e) => e.chain().focus().toggleBulletList().run(),
    gap: true,
  },
  {
    label: '1. Lista',
    title: 'Lista numerada',
    active: (e) => e.isActive('orderedList'),
    run: (e) => e.chain().focus().toggleOrderedList().run(),
  },
  {
    label: 'Citação',
    title: 'Citação',
    active: (e) => e.isActive('blockquote'),
    run: (e) => e.chain().focus().toggleBlockquote().run(),
  },
  {
    label: 'Desfazer',
    title: 'Desfazer',
    run: (e) => e.chain().focus().undo().run(),
    gap: true,
  },
];

const Toolbar: React.FC<{ editor: Editor | null }> = ({ editor }) => {
  const [, force] = React.useReducer((n) => n + 1, 0);
  React.useEffect(() => {
    if (!editor) return;
    editor.on('transaction', force);
    return () => void editor.off('transaction', force);
  }, [editor]);
  return (
    <div className="flex flex-wrap items-center gap-1 border-b border-border bg-sunken px-4 py-2.5">
      {TOOLS.map((t) => {
        const on = !!editor && !!t.active?.(editor);
        return (
          <button
            key={t.label}
            type="button"
            title={t.title}
            disabled={!editor}
            onClick={() => editor && t.run(editor)}
            className={cn(
              'flex h-8 min-w-[34px] items-center justify-center rounded-lg border px-[9px] text-[12.5px] font-semibold transition-colors hover:bg-raised',
              on
                ? 'border-olive-line bg-accent text-olive-ink'
                : 'border-transparent text-muted-foreground',
              t.gap && 'ml-2.5',
              t.className,
            )}
          >
            {t.label}
          </button>
        );
      })}
      <span className="col-label ml-auto text-faint">
        {editor ? 'Texto rico' : 'Carregando editor…'}
      </span>
    </div>
  );
};

// Tipografia do conteúdo (editor E leitura): `.bt-prose` em styles/globals.css.
